/**
 * FIR design.
 *
 * Two jobs:
 *  1. Reconstruction / interpolation filters for the DAC model. The audible
 *     differences between DAC filter options are transition width, stopband
 *     depth and phase, so the designer is parameterised on exactly those.
 *  2. Arbitrary-magnitude filters built from a frequency-response curve, used
 *     for loudspeaker and headphone responses. This is the path a MEASURED
 *     response takes, so it must be the same code the modelled one uses.
 */

import { getFFT, nextPow2 } from './fft';
import type { FRCurve } from '../types/curves';
import { evalFR } from '../types/curves';

// ---------------------------------------------------------------------------
// Windows
// ---------------------------------------------------------------------------

export function kaiserBeta(stopbandDb: number): number {
  const a = Math.abs(stopbandDb);
  if (a > 50) return 0.1102 * (a - 8.7);
  if (a >= 21) return 0.5842 * Math.pow(a - 21, 0.4) + 0.07886 * (a - 21);
  return 0;
}

function besselI0(x: number): number {
  let sum = 1;
  let term = 1;
  const half = x / 2;
  for (let k = 1; k < 60; k++) {
    term *= (half / k) * (half / k);
    sum += term;
    if (term < sum * 1e-14) break;
  }
  return sum;
}

export function kaiserWindow(n: number, beta: number): Float64Array {
  const w = new Float64Array(n);
  const denom = besselI0(beta);
  const m = n - 1;
  for (let i = 0; i < n; i++) {
    const r = (2 * i) / m - 1;
    w[i] = besselI0(beta * Math.sqrt(Math.max(0, 1 - r * r))) / denom;
  }
  return w;
}

export function blackmanHarris(n: number): Float64Array {
  const w = new Float64Array(n);
  const a = [0.35875, 0.48829, 0.14128, 0.01168];
  for (let i = 0; i < n; i++) {
    const t = (2 * Math.PI * i) / (n - 1);
    w[i] = a[0] - a[1] * Math.cos(t) + a[2] * Math.cos(2 * t) - a[3] * Math.cos(3 * t);
  }
  return w;
}

export function hann(n: number): Float64Array {
  const w = new Float64Array(n);
  for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1));
  return w;
}

export function hamming(n: number): Float64Array {
  const w = new Float64Array(n);
  for (let i = 0; i < n; i++) w[i] = 0.54 - 0.46 * Math.cos((2 * Math.PI * i) / (n - 1));
  return w;
}

// ---------------------------------------------------------------------------
// Windowed-sinc lowpass
// ---------------------------------------------------------------------------

export interface LowpassSpec {
  /** Number of taps (forced odd for a symmetric linear-phase design). */
  taps: number;
  /** Cutoff as a fraction of the sample rate (0..0.5). */
  cutoff: number;
  /** Stopband attenuation in dB (positive number). */
  stopbandDb: number;
}

/** Linear-phase windowed-sinc lowpass, normalised to unity DC gain. */
export function designLowpass({ taps, cutoff, stopbandDb }: LowpassSpec): Float32Array {
  const n = taps % 2 === 0 ? taps + 1 : taps;
  const out = new Float32Array(n);
  const w = kaiserWindow(n, kaiserBeta(stopbandDb));
  const mid = (n - 1) / 2;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const k = i - mid;
    const s = k === 0 ? 2 * cutoff : Math.sin(2 * Math.PI * cutoff * k) / (Math.PI * k);
    const v = s * w[i];
    out[i] = v;
    sum += v;
  }
  if (sum !== 0) for (let i = 0; i < n; i++) out[i] /= sum;
  return out;
}

/**
 * Apodizing lowpass: a Gaussian-tapered sinc. Trades transition steepness for
 * the absence of pre-ringing in the (still linear-phase) impulse response.
 */
export function designApodizing(taps: number, cutoff: number): Float32Array {
  const n = taps % 2 === 0 ? taps + 1 : taps;
  const out = new Float32Array(n);
  const mid = (n - 1) / 2;
  const sigma = n / 6.5;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const k = i - mid;
    const s = k === 0 ? 2 * cutoff : Math.sin(2 * Math.PI * cutoff * k) / (Math.PI * k);
    const g = Math.exp(-0.5 * (k / sigma) * (k / sigma));
    const v = s * g;
    out[i] = v;
    sum += v;
  }
  if (sum !== 0) for (let i = 0; i < n; i++) out[i] /= sum;
  return out;
}

// ---------------------------------------------------------------------------
// Minimum phase
// ---------------------------------------------------------------------------

/**
 * Convert a linear-phase (or arbitrary) FIR to its minimum-phase equivalent
 * with the same magnitude response, via the real cepstrum / Hilbert method.
 *
 * This is the standard homomorphic construction: take log|H|, fold the cepstrum
 * to be causal, exponentiate.
 */
export function toMinimumPhase(taps: Float32Array, fftSize?: number): Float32Array {
  const n = fftSize ?? nextPow2(Math.max(taps.length * 16, 4096));
  const fft = getFFT(n);
  const re = new Float32Array(n);
  const im = new Float32Array(n);
  re.set(taps);
  fft.forward(re, im);

  // Real cepstrum of the log-magnitude.
  for (let i = 0; i < n; i++) {
    const mag = Math.hypot(re[i], im[i]);
    re[i] = Math.log(Math.max(1e-12, mag));
    im[i] = 0;
  }
  fft.inverse(re, im);

  // Fold: keep n=0, double 0<n<N/2, zero the rest.
  const half = n / 2;
  for (let i = 1; i < half; i++) re[i] *= 2;
  re[half] *= 1;
  for (let i = half + 1; i < n; i++) re[i] = 0;
  im.fill(0);

  fft.forward(re, im);
  // exp of the complex log
  for (let i = 0; i < n; i++) {
    const ex = Math.exp(re[i]);
    re[i] = ex * Math.cos(im[i]);
    im[i] = ex * Math.sin(im[i]);
  }
  fft.inverse(re, im);

  // Truncate back to a sensible length: energy is now front-loaded.
  const outLen = Math.min(n, Math.max(taps.length, 64));
  const out = new Float32Array(outLen);
  out.set(re.subarray(0, outLen));
  return out;
}

/**
 * Hybrid phase: crossfade between the linear-phase and minimum-phase impulse
 * responses. Several converters expose exactly this as a filter option.
 */
export function toHybridPhase(taps: Float32Array, mix = 0.5): Float32Array {
  const min = toMinimumPhase(taps);
  const len = Math.max(taps.length, min.length);
  const out = new Float32Array(len);
  // Align by peak so the crossfade does not smear the main lobe.
  const peakLin = argMaxAbs(taps);
  const peakMin = argMaxAbs(min);
  for (let i = 0; i < len; i++) {
    const a = taps[i - (peakLin - peakMin)] ?? 0;
    const b = min[i] ?? 0;
    out[i] = (1 - mix) * a + mix * b;
  }
  return normalizeDc(out);
}

function argMaxAbs(x: Float32Array): number {
  let best = 0;
  let bi = 0;
  for (let i = 0; i < x.length; i++) {
    const a = Math.abs(x[i]);
    if (a > best) { best = a; bi = i; }
  }
  return bi;
}

export function normalizeDc(x: Float32Array): Float32Array {
  let s = 0;
  for (let i = 0; i < x.length; i++) s += x[i];
  if (Math.abs(s) > 1e-12) for (let i = 0; i < x.length; i++) x[i] /= s;
  return x;
}

export function normalizePeakGain(x: Float32Array, targetDb = 0): Float32Array {
  // Normalise so the magnitude at 1 kHz-ish (bin 0 of a long FFT ~ DC) is target.
  let s = 0;
  for (let i = 0; i < x.length; i++) s += x[i];
  const g = Math.pow(10, targetDb / 20) / (Math.abs(s) > 1e-12 ? s : 1);
  for (let i = 0; i < x.length; i++) x[i] *= g;
  return x;
}

// ---------------------------------------------------------------------------
// Arbitrary magnitude FIR from an FR curve
// ---------------------------------------------------------------------------

export interface CurveFirOptions {
  taps: number;
  sampleRate: number;
  /** 'minimum' produces a causal minimum-phase FIR; 'linear' a symmetric one. */
  phase: 'minimum' | 'linear';
  /** Extra gain in dB applied to the whole response. */
  gainDb?: number;
  /** Clamp the designed response to +/- this many dB to avoid absurd boosts. */
  clampDb?: number;
  /** Optional explicit phase curve in degrees, overrides `phase`. */
  phaseCurve?: (freqHz: number) => number;
}

/**
 * Frequency-sampling design: sample the target magnitude onto a linear grid,
 * build a zero-phase (or supplied-phase) spectrum, inverse transform, window.
 *
 * This is the function a measured FR CSV flows through, unchanged.
 */
export function designFromCurve(curve: FRCurve | undefined, opts: CurveFirOptions): Float32Array {
  const { taps, sampleRate, phase, gainDb = 0, clampDb = 36 } = opts;
  const n = nextPow2(Math.max(taps * 8, 2048));
  const fft = getFFT(n);
  const re = new Float32Array(n);
  const im = new Float32Array(n);
  const half = n / 2;

  for (let i = 0; i <= half; i++) {
    const f = (i / n) * sampleRate;
    let db = evalFR(curve, Math.max(1, f)) + gainDb;
    if (db > clampDb) db = clampDb;
    if (db < -clampDb) db = -clampDb;
    const mag = Math.pow(10, db / 20);
    if (opts.phaseCurve) {
      const ph = (opts.phaseCurve(Math.max(1, f)) * Math.PI) / 180;
      re[i] = mag * Math.cos(ph);
      im[i] = mag * Math.sin(ph);
    } else {
      re[i] = mag;
      im[i] = 0;
    }
  }
  // Hermitian symmetry for a real impulse response.
  for (let i = half + 1; i < n; i++) {
    re[i] = re[n - i];
    im[i] = -im[n - i];
  }

  fft.inverse(re, im);

  const len = taps % 2 === 0 ? taps + 1 : taps;
  const out = new Float32Array(len);
  const mid = (len - 1) / 2;
  const w = blackmanHarris(len);
  // Zero-phase impulse response is circularly centred at 0; rotate to centre.
  for (let i = 0; i < len; i++) {
    const src = (i - mid + n) % n;
    out[i] = re[src] * w[i];
  }

  if (phase === 'minimum') {
    return toMinimumPhase(out, nextPow2(Math.max(len * 16, 8192)));
  }
  return out;
}

/**
 * Build the DAC reconstruction FIR for a given filter option, at the
 * oversampled rate.
 */
export interface ReconstructionSpec {
  mode: string;
  taps: number;
  /** Cutoff relative to the ORIGINAL Nyquist (1.0 = original Nyquist). */
  cutoffRatio: number;
  transitionRatio: number;
  stopbandDb: number;
  /** Oversampling factor the filter runs at. */
  oversample: number;
}

export function designReconstruction(spec: ReconstructionSpec): { taps: Float32Array; latency: number } {
  const os = Math.max(1, spec.oversample);
  // Cutoff expressed as a fraction of the OVERSAMPLED rate.
  const cutoff = (spec.cutoffRatio * 0.5) / os;
  const stop = Math.abs(spec.stopbandDb);
  const taps = Math.max(9, Math.round(spec.taps * os));

  let h: Float32Array;
  switch (spec.mode) {
    case 'apodizing':
      h = designApodizing(taps, cutoff);
      break;
    case 'minimum-phase':
    case 'slow-minimum':
      h = toMinimumPhase(designLowpass({ taps, cutoff, stopbandDb: stop }));
      break;
    case 'hybrid':
      h = toHybridPhase(designLowpass({ taps, cutoff, stopbandDb: stop }), 0.55);
      break;
    default:
      h = designLowpass({ taps, cutoff, stopbandDb: stop });
      break;
  }

  // Interpolation filters must have unity gain per output sample after
  // zero-stuffing, which multiplies amplitude by 1/os.
  for (let i = 0; i < h.length; i++) h[i] *= os;

  const isLinear = spec.mode !== 'minimum-phase' && spec.mode !== 'slow-minimum' && spec.mode !== 'hybrid';
  const latency = isLinear ? (h.length - 1) / 2 : estimateLatency(h);
  return { taps: h, latency };
}

function estimateLatency(h: Float32Array): number {
  // Energy centroid of the first 90% of cumulative energy.
  let total = 0;
  for (let i = 0; i < h.length; i++) total += h[i] * h[i];
  let acc = 0;
  for (let i = 0; i < h.length; i++) {
    acc += h[i] * h[i];
    if (acc >= total * 0.5) return i;
  }
  return 0;
}

/**
 * Zero-order-hold droop compensation for NOS playback: an inverse-sinc FIR.
 * `order` 0 = none, 1 = partial, 2 = full compensation to Nyquist.
 */
export function designSincCompensation(taps: number, sampleRate: number, order: number): Float32Array {
  if (order <= 0) return new Float32Array([1]);
  const n = nextPow2(Math.max(taps * 8, 1024));
  const fft = getFFT(n);
  const re = new Float32Array(n);
  const im = new Float32Array(n);
  const half = n / 2;
  const strength = Math.min(1, order / 2);
  for (let i = 0; i <= half; i++) {
    const x = (Math.PI * i) / n;
    const sinc = i === 0 ? 1 : Math.sin(x) / x;
    // Do not attempt infinite boost at Nyquist.
    const target = Math.min(4, 1 / Math.max(0.25, sinc));
    const mag = 1 + (target - 1) * strength;
    re[i] = mag;
    im[i] = 0;
  }
  for (let i = half + 1; i < n; i++) {
    re[i] = re[n - i];
    im[i] = 0;
  }
  fft.inverse(re, im);
  const len = taps % 2 === 0 ? taps + 1 : taps;
  const out = new Float32Array(len);
  const mid = (len - 1) / 2;
  const w = blackmanHarris(len);
  for (let i = 0; i < len; i++) out[i] = re[(i - mid + n) % n] * w[i];
  return out;
}
